import { Info, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@ui/skeleton.tsx';
import { Switch } from '@ui/switch.tsx';

/** Introduces the feature settings list and explains its workspace scope. */
export function FeaturesIntroduction() {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <SlidersHorizontal className="size-6" aria-hidden />
        <h2 className="text-2xl font-bold">{t('settings.features.list.title')}</h2>
      </div>
      <p className="text-muted-foreground">{t('settings.features.list.description')}</p>
      <div className="flex items-start gap-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        <p>{t('settings.features.help')}</p>
      </div>
    </div>
  );
}

type FeatureSettingsSectionProps = {
  title: string;
  description: string;
  toggleLabel: string;
  toggleAriaLabel: string;
  checked: boolean;
  isLoading: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** Renders a reusable feature setting with its description and toggle state. */
export function FeatureSettingsSection({
  title,
  description,
  toggleLabel,
  toggleAriaLabel,
  checked,
  isLoading,
  disabled,
  onCheckedChange
}: FeatureSettingsSectionProps) {
  return (
    <div className="space-y-3">
      <h3 className="text-xl font-bold">{title}</h3>
      <p className="max-w-5xl text-muted-foreground">{description}</p>
      <div className="flex items-center gap-3 pt-2">
        {isLoading ? (
          <Skeleton className="h-6 w-11 rounded-full" />
        ) : (
          <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-label={toggleAriaLabel}
          />
        )}
        <span className="font-medium">{toggleLabel}</span>
      </div>
    </div>
  );
}
